import { useWindowSize, useScrollLock } from "@vueuse/core";
import PullToRefresh from "pulltorefreshjs";
import { computed, ref, watch } from "vue";

declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

const CLASSES_FOR_BLOCK = [".ui-drawer", ".ui-bottom-sheet"];

const isBlockedPWAScroll = ref(false);
const isPTRActive = ref(false);
const lastTouchTarget = ref<EventTarget | null>(null);
const lockScrollHTML = useScrollLock(document.querySelector("html"));

/**
 * Настройка библ.PullToRefresh для использования в PWA
 */
const usePullToRefreshPWA = () => {
  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  if (isPWA && !isPTRActive.value) {
    document.addEventListener(
      "touchstart",
      (e) => {
        lastTouchTarget.value = e.target;
      },
      { passive: true }
    );

    PullToRefresh.init({
      mainElement: "#app",
      onRefresh() {
        window.location.reload();
      },
      shouldPullToRefresh() {
        return (
          window.scrollY === 0 &&
          !isBlockedPWAScroll.value &&
          !isInsideScrollableElement(lastTouchTarget.value)
        );
      },
      instructionsPullToRefresh: "Потяните вниз, чтобы обновить",
      instructionsReleaseToRefresh: "Отпустите для обновления",
      instructionsRefreshing: "Обновление...",
    });

    isPTRActive.value = true;
  }
};

/**
 * Проверка для PWA
 * Имеется ли скролл на касаемом элементе
 * @returns true - скрол элемента (игнорирование PTR)
 */
const isInsideScrollableElement = (el: EventTarget | null) => {
  let current = el as HTMLElement | null;

  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const isScrollable =
      (/(auto|scroll)/.test(style.overflowY) ||
        /(auto|scroll)/.test(style.overflow)) &&
      current.scrollHeight > current.clientHeight;

    if (isScrollable) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};

/**
 * Проверка элемента, является ли он дровером || bottom-sheet
 * @return true - блокировать / разблокировать скролл в pwa & browser
 */
const checkAndChangeScroll = (node: Node, isBlockScroll: boolean) => {
  if (
    node instanceof HTMLElement &&
    CLASSES_FOR_BLOCK.some((el) => node.matches(el) || node.querySelector(el))
  ) {
    lockScrollHTML.value = isBlockScroll;
    isBlockedPWAScroll.value = isBlockScroll;
  }
};

/**
 * Отслеживание добавления / удаления DOM узлов
 */
const useMutationObserver = () => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        checkAndChangeScroll(node, true);
      });
      mutation.removedNodes.forEach((node) => {
        checkAndChangeScroll(node, false);
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

export const usePullToRefresh = () => {
  const { width } = useWindowSize();
  const isMobileView = computed(() => width.value <= 767);

  watch(
    isMobileView,
    (isMobile) => {
      if (isMobile) {
        useMutationObserver();
        usePullToRefreshPWA();
      }
    },
    { immediate: true }
  );
};
